import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaFileContract, FaTag } from "react-icons/fa";
import { getPurchaseOrders, getPurchaseOrderInbox } from "redux/erp/purchaseOrderSlice";
import { EmptyState, LoadingSpinner } from "components/common";
import PageShell, { PageTitle } from "../purchase/common/PageShell";
import FilterDropdown from "../purchase/common/FilterDropdown";
import Clickable from "../purchase/common/Clickable";

const initialsOf = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();
const money = (v) => (Number(v) || 0).toLocaleString("en-US");

const STATUS = {
  0: { name: "Draft", dot: "tw-bg-geist-fg-3", cls: "tw-bg-geist-100 tw-text-geist-fg-2 tw-border-geist-border" },
  1: { name: "Pending", dot: "tw-bg-geist-warning", cls: "tw-bg-[#fff8ee] tw-text-[#b46a00] tw-border-[#f7e2c0]" },
  2: { name: "Approved", dot: "tw-bg-geist-success", cls: "tw-bg-[#e9f9f6] tw-text-[#04766a] tw-border-[#bfeae4]" },
  3: { name: "Rejected", dot: "tw-bg-geist-error", cls: "tw-bg-[#fdeceb] tw-text-[#c0362f] tw-border-[#f6cfcc]" },
  4: { name: "Canceled", dot: "tw-bg-geist-fg-3", cls: "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border" },
};
const STATUS_OPTIONS = [0, 1, 2, 3, 4].map((id) => ({ value: id, label: STATUS[id].name }));

const TH = "tw-px-4 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-geist-fg-3 tw-whitespace-nowrap tw-bg-geist-bg-2";

// PO chỉ được tạo TỪ trang chi tiết của 1 Purchase Requisition đã approved (nút
// "Create Purchase Order") — trang list này chỉ hiển thị + duyệt, không có "New" ở đây.
const PurchaseOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allData, inboxData, loading } = useSelector((s) => s.erp_purchase_order);

  const [mode, setMode] = useState("mine");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);

  const fetchList = () => {
    if (mode === "inbox") dispatch(getPurchaseOrderInbox());
    else dispatch(getPurchaseOrders({}));
  };
  useEffect(fetchList, [dispatch, mode]);

  const rawRows = useMemo(
    () => ((mode === "inbox" ? inboxData?.rows : allData?.rows) || []).map((r) => r.attributes || r),
    [mode, inboxData, allData]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rawRows.filter((a) => {
      const sid = a.status?.id ?? a.status;
      if (mode === "mine" && statusFilter.length && !statusFilter.includes(sid)) return false;
      if (!q) return true;
      return [a.code, a.supplier_name, a.requestor, a.site_name, a.purchase_request_code, a.current_approver_email]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rawRows, search, statusFilter, mode]);

  const header = (
    <div className="tw-px-4 sm:tw-px-6 tw-pt-4 tw-pb-3">
      <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3 tw-mb-3">
        <PageTitle subtitle="Purchase Order — QF-PUR-03">Purchase Orders</PageTitle>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-2 tw-h-9 tw-w-64 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border-2 focus-within:tw-border-geist-accent tw-transition-colors">
            <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, supplier, PR..."
              className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
            />
            {search && (
              <Clickable onClick={() => setSearch("")} className="tw-text-geist-fg-3 hover:tw-text-geist-fg">
                <i className="bi bi-x-lg tw-text-[11px]" />
              </Clickable>
            )}
          </div>
          <Clickable onClick={() => navigate("/purchase-requests")}
            title="A PO must be created from an approved Purchase Requisition"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2 tw-transition-colors">
            <i className="bi bi-file-earmark-plus tw-text-sm" /> Go to Purchase Requisitions
          </Clickable>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <div className="tw-inline-flex tw-p-0.5 tw-bg-geist-100 tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm">
          {[
            { k: "mine", label: "My orders", icon: "bi-person-lines-fill" },
            { k: "inbox", label: "Waiting for my signature", icon: "bi-pen" },
          ].map((m) => (
            <Clickable key={m.k} onClick={() => setMode(m.k)}
              className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-[4px] tw-transition ${
                mode === m.k ? "tw-bg-geist-bg tw-text-geist-fg tw-shadow-geist-card" : "tw-text-geist-fg-3 hover:tw-text-geist-fg"
              }`}>
              <i className={`bi ${m.icon} tw-text-[11px]`} /> {m.label}
            </Clickable>
          ))}
        </div>

        {mode === "mine" && (
          <FilterDropdown label="Status" icon={FaTag} value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
        )}

        <span className="tw-text-xs tw-text-geist-fg-3 tw-ml-auto">{rows.length} item(s)</span>
      </div>
    </div>
  );

  return (
    <PageShell header={header}>
      {loading ? (
        <LoadingSpinner message="Loading..." />
      ) : rows.length === 0 ? (
        <EmptyState icon={FaFileContract}
          title={search || statusFilter.length ? "No matching orders" : "No purchase orders"}
          description={search || statusFilter.length ? "Try changing the search or status filter." : "Create one from an approved Purchase Requisition."} />
      ) : (
        <div className="tw-p-4 sm:tw-p-6">
          <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-overflow-hidden">
            <table className="tw-w-full tw-border-collapse">
              <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="tw-border-b tw-border-solid tw-border-geist-border">
                  <th className={TH}>Order</th>
                  <th className={TH}>Purchase Requisition</th>
                  <th className={TH}>Prepared by</th>
                  {mode === "inbox" && <th className={TH}>Pending signer</th>}
                  <th className={`${TH} tw-text-right`}>Total</th>
                  <th className={TH}>Status</th>
                  <th className={`${TH} tw-w-8`} />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const st = STATUS[a.status?.id ?? a.status] || STATUS[0];
                  return (
                    <tr key={a.id} onClick={() => navigate(`/purchase-orders/${a.id}`)}
                      className="tw-group tw-border-b tw-border-solid tw-border-geist-border last:tw-border-0 hover:tw-bg-geist-bg-2 tw-cursor-pointer tw-transition-colors">
                      <td className="tw-px-4 tw-py-3 tw-align-middle">
                        <div className="tw-flex tw-items-center tw-gap-2.5">
                          <div className="tw-shrink-0 tw-w-9 tw-h-9 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-semibold tw-uppercase">
                            {initialsOf(a.supplier_name)}
                          </div>
                          <div className="tw-min-w-0 tw-flex-1">
                            <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg tw-truncate">{a.code}</span>
                            <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate">{a.supplier_name || "—"}</div>
                          </div>
                        </div>
                      </td>
                      <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-[13px] tw-text-geist-fg-2">{a.purchase_request_code || "—"}</td>
                      <td className="tw-px-4 tw-py-3 tw-align-middle">
                        <div className="tw-text-[13px] tw-text-geist-fg tw-truncate">{a.requestor || "—"}</div>
                        {a.created_date && <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate">{a.created_date}</div>}
                      </td>
                      {mode === "inbox" && (
                        <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-[12px] tw-text-geist-fg-2 tw-truncate">
                          {a.current_approver_email || "—"}
                        </td>
                      )}
                      <td className="tw-px-4 tw-py-3 tw-align-middle tw-text-right tw-tabular-nums tw-text-[13px] tw-font-semibold tw-text-geist-fg">
                        {money(a.total)}
                      </td>
                      <td className="tw-px-4 tw-py-3 tw-align-middle">
                        <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-xl tw-border tw-border-solid ${st.cls}`}>
                          <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-xl ${st.dot}`} />
                          {st.name}
                        </span>
                      </td>
                      <td className="tw-px-2 tw-py-3 tw-align-middle tw-text-right">
                        <i className="bi bi-chevron-right tw-text-[11px] tw-text-geist-fg-3 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default PurchaseOrders;
